/**
 * @fileoverview Unrouted Emails page for superusers to assign or dismiss
 * emails that could not be automatically routed to an organization.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Email as EmailIcon,
  Business as BusinessIcon,
  DeleteOutline as DismissIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const UnroutedEmails = () => {
  const [emails, setEmails] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Assign dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Dismiss dialog state
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false);
  const [emailToDismiss, setEmailToDismiss] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [emailsRes, orgsRes] = await Promise.all([
        api.get('/v1/gmail/unrouted/'),
        api.get('/v1/organizations/'),
      ]);
      setEmails(emailsRes.data);
      setOrganizations(orgsRes.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch unrouted emails');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssignDialog = (email) => {
    setSelectedEmail(email);
    setSelectedOrgId('');
    setAssignDialogOpen(true);
  };

  const handleAssign = async () => {
    if (!selectedOrgId || !selectedEmail) return;

    try {
      setAssigning(true);
      await api.post(`/v1/gmail/unrouted/${selectedEmail.id}/assign/`, {
        organization_id: selectedOrgId,
      });
      setEmails((prev) => prev.filter((e) => e.id !== selectedEmail.id));
      setSnackbar({ open: true, message: 'Email assigned successfully', severity: 'success' });
      setAssignDialogOpen(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to assign email',
        severity: 'error',
      });
    } finally {
      setAssigning(false);
    }
  };

  const handleOpenDismissDialog = (email) => {
    setEmailToDismiss(email);
    setDismissDialogOpen(true);
  };

  const handleDismiss = async () => {
    if (!emailToDismiss) return;

    try {
      setDismissing(true);
      await api.post(`/v1/gmail/unrouted/${emailToDismiss.id}/dismiss/`);
      setEmails((prev) => prev.filter((e) => e.id !== emailToDismiss.id));
      setSnackbar({ open: true, message: 'Email dismissed', severity: 'success' });
      setDismissDialogOpen(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to dismiss email',
        severity: 'error',
      });
    } finally {
      setDismissing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const truncate = (str, maxLen = 80) => {
    if (!str) return '-';
    return str.length > maxLen ? str.substring(0, maxLen) + '...' : str;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <EmailIcon sx={{ mr: 1, fontSize: 28, color: 'primary.main' }} />
        <Typography variant="h5" fontWeight="bold">
          Unrouted Emails
        </Typography>
        <Chip label={emails.length} size="small" color="primary" sx={{ ml: 2 }} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {emails.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <EmailIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            No unrouted emails
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All incoming emails have been routed to an organization.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>From</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Body Snippet</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emails.map((email) => (
                <TableRow key={email.id} hover>
                  <TableCell>{email.from_address || email.sender || '-'}</TableCell>
                  <TableCell>
                    <Tooltip title={email.subject || ''}>
                      <span>{truncate(email.subject, 50)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDate(email.received_at || email.date)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={email.body_snippet || email.body || ''}>
                      <span>{truncate(email.body_snippet || email.body)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<BusinessIcon />}
                      onClick={() => handleOpenAssignDialog(email)}
                      sx={{ mr: 1 }}
                    >
                      Assign
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      startIcon={<DismissIcon />}
                      onClick={() => handleOpenDismissDialog(email)}
                    >
                      Dismiss
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Email to Organization</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select an organization to route this email to:
          </Typography>
          {selectedEmail && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2"><strong>From:</strong> {selectedEmail.from_address || selectedEmail.sender || '-'}</Typography>
              <Typography variant="body2"><strong>Subject:</strong> {selectedEmail.subject || '-'}</Typography>
            </Box>
          )}
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Organization</InputLabel>
            <Select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              label="Organization"
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name} {org.code ? `(${org.code})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assigning}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            variant="contained"
            disabled={!selectedOrgId || assigning}
            startIcon={assigning ? <CircularProgress size={16} /> : null}
          >
            {assigning ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dismiss Confirmation Dialog */}
      <Dialog open={dismissDialogOpen} onClose={() => setDismissDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Dismiss Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to dismiss this email? It will be marked as dismissed and removed from the list.
          </Typography>
          {emailToDismiss && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2"><strong>From:</strong> {emailToDismiss.from_address || emailToDismiss.sender || '-'}</Typography>
              <Typography variant="body2"><strong>Subject:</strong> {emailToDismiss.subject || '-'}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDismissDialogOpen(false)} disabled={dismissing}>
            Cancel
          </Button>
          <Button
            onClick={handleDismiss}
            variant="contained"
            color="error"
            disabled={dismissing}
            startIcon={dismissing ? <CircularProgress size={16} /> : null}
          >
            {dismissing ? 'Dismissing...' : 'Dismiss'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UnroutedEmails;
